import { AsyncPaymentResult } from '../../types/AsyncPaymentResult';
import { createExpectedError } from '../expectedError';
import {
  parseAsyncPaymentResultStr,
  paymentFinishMessage,
  popupPingMessage,
  popupRedirectMessage,
  PostMessageFromPopup,
  windowFeatures,
} from './utils';
import { win } from '../../../utils/win';

export const isAliveInterval: number = 100;
export const periodicPingInterval: number = 100;

type OnCloseListener = (isPaymentAborted?: boolean) => void;

class PaymentPopup {
  private popupWindow: Window;
  private closeListenerIntervalHandler: number = null;
  private periodicPingHandler: number = null;
  private onCloseListeners = new Set<OnCloseListener>();
  private messageListener: (event: PostMessageFromPopup) => void;

  // need to ping the popup with so when payment is completed popup will have the link to
  // widget win (taking event.source) and will send postMessage with payment status to this source
  // (We can't rely on win.opener, because it can be nullified by Payment Provider page when they have control)
  private startPeriodicPing() {
    this.periodicPingHandler = win.setInterval(() => {
      if (
        this.isOpened() &&
        typeof this.popupWindow?.postMessage === 'function'
      ) {
        this.popupWindow?.postMessage(
          {
            type: popupPingMessage,
          },
          '*'
        );
      }
    }, periodicPingInterval);
  }

  private stopPeriodicPing() {
    if (this.periodicPingHandler !== null) {
      win.clearInterval(this.periodicPingHandler);
      this.periodicPingHandler = null;
    }
  }

  public listenClose(cb: OnCloseListener) {
    this.onCloseListeners.add(cb);

    if (this.closeListenerIntervalHandler === null) {
      this.closeListenerIntervalHandler = win.setInterval(() => {
        if (!this.isOpened()) {
          this.onCloseListeners.forEach((listener) => listener(true));
          this.cleanup();
        }
      }, isAliveInterval);
    }
  }

  private unlistenClose() {
    this.onCloseListeners.clear();

    if (this.closeListenerIntervalHandler !== null) {
      win.clearInterval(this.closeListenerIntervalHandler);
      this.closeListenerIntervalHandler = null;
    }
  }

  public listenPaymentFinish(cb: (result: AsyncPaymentResult) => void) {
    this.unlistenPaymentFinish();

    this.messageListener = (event: PostMessageFromPopup) => {
      const isSentFromPaymentPopup =
        process.env.NODE_ENV !== 'production' ||
        (new URL('https://static.parastorage.com').origin === event.origin &&
          event.source === this.popupWindow);

      if (isSentFromPaymentPopup && event.data.type === paymentFinishMessage) {
        this.unlistenPaymentFinish();
        cb(parseAsyncPaymentResultStr(event.data.payload));
      }
    };

    win.addEventListener('message', this.messageListener);
  }

  private unlistenPaymentFinish() {
    if (this.messageListener) {
      win.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  public isOpened(): boolean {
    return !!this.popupWindow && !this.popupWindow.closed;
  }

  public open(params: {
    url: string;
    sizes: { height: number; width: number };
  }) {
    const { url, sizes } = params;

    if (this.isOpened()) {
      console.warn(
        `Payment Popup is already opened - closing (aborting) previous one`
      );
      this.close(true);
    }

    this.popupWindow = win.open(url, '_blank', windowFeatures(sizes));
    this.startPeriodicPing();
  }

  public redirectTo(url: string) {
    if (!this.isOpened()) {
      throw createExpectedError({
        errorMessage: `Failed to redirect the popup - it's not opened`,
      });
    }

    // we expect "processingPageUrl" (our page with Spinner served from CDN) to be opened
    // and it listens to this message
    this.popupWindow.postMessage?.(
      {
        type: popupRedirectMessage,
        url,
      },
      '*'
    );
  }

  public close(isPaymentAborted: boolean = false) {
    if (this.isOpened()) {
      this.popupWindow.close();
      this.onCloseListeners.forEach((listener) => listener(isPaymentAborted));
      this.cleanup();
    }
  }

  private cleanup() {
    this.unlistenClose();
    this.unlistenPaymentFinish();
    this.stopPeriodicPing();
    this.popupWindow = null;
  }
}

export const paymentPopup = new PaymentPopup();
